// TASKS IN AN AI ANSWER — the views the assistant mounts in its thread and a
// Fly page places as sections (workspace functions/assistantAppViews.js, the
// `views[]` contract).
//
// ticketsAiViews() is the tickets view PROVIDER, read from the EAGER slice
// (the tickets package is lazy — the shell folds its context before a view
// mounts, `fold: 'tickets'`). Two views:
//
//   tickets-board    the tasks that match the filters, in one of three
//                    layouts: a LIST (the home widget kit's row: title,
//                    status · priority), a BOARD (every status column the
//                    status covers, each with its full count and its first
//                    cards — the app's own TicketsCard) or the CANVAS (the
//                    tasks where the /tasks board places them on its canvas,
//                    the board's own MyTicketCard, read-only and scaled to the
//                    card). A row or a card opens the task's own detail
//                    sheet; nothing here writes.
//   tickets-roadmap  the cycles and epics on a timeline
//                    (functions/ticketsRoadmapAnswer.js).
//
// The reads are the board's own (ticketLoaders fetchTicketsForAnswer,
// fetchColumnPage, fetchColumnCounts), scoped by the server to the member's
// workspace and grants. The names an ask gives — a project, an epic, a cycle,
// a teammate — resolve to what the reads filter on BEFORE they run; a name
// nothing here answers to matches nothing, never every task. ↗ Open opens the
// /tasks board with the same filters, as far as the board can show them (the
// view's `openRoute`).
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function ticketsAiViews() {
  return {
    app: {
      id: 'system/tickets',
      navbar: [{ label: 'tickets.answer.title', route: '/tasks', icon: 'check' }],
      apps: [{ icon: 'check' }]
    },
    fold: 'tickets',
    views: [
      {
        id: 'tickets-board',
        title: 'tickets.answer.title',
        // The ONE line the model matches an ask against (the server caps it at
        // 160 characters).
        describe:
          'Tasks as a list, status columns or the board canvas; by status, people, priority, type, project, epic, cycle, due date, title words.',
        component: 'TicketsAnswerView',
        icon: 'check',
        hue: 'tickets',
        size: 'l',
        // Twelve params: the server keeps twelve per view.
        params: {
          status: {
            type: 'enum',
            values: [
              'open',
              'all',
              'draft',
              'backlog',
              'todo',
              'in_progress',
              'blocked',
              'ready_to_test',
              'review',
              'done'
            ],
            default: 'open',
            describe:
              'open = not done and not a draft (default); all = every task; draft = tasks outside a board column'
          },
          // Live on dev: "What tasks are due this week?" came back assignee
          // 'me' — nobody said "my" — and hid the workspace's one open task.
          assignee: {
            type: 'string',
            describe:
              "'me' only for me, my or mine; 'team', 'agents', or a teammate's name or email; else leave it out"
          },
          priority: { type: 'enum', values: ['P0', 'P1', 'P2', 'P3'] },
          type: { type: 'enum', values: ['bug', 'task', 'user_story', 'epic'] },
          // QA on dev: "What tasks are open for the docs project" matched the
          // word in the titles — the task↔project link is a filter of its own.
          project: {
            type: 'string',
            describe:
              "a project's name or key: the tasks linked to that project, never words in a title"
          },
          epic: { type: 'string', describe: "an epic's name, only when the ask names one" },
          cycle: { type: 'string', describe: "a cycle's name, or 'current' for the active cycle" },
          due: {
            type: 'enum',
            values: ['overdue', 'today', 'thisWeek', 'nextWeek', 'none'],
            // Bundle keys: the catalog sends them as { en, ka } words.
            labels: {
              overdue: 'tickets.answer.due.overdue',
              today: 'tickets.answer.due.today',
              thisWeek: 'tickets.answer.due.thisWeek',
              nextWeek: 'tickets.answer.due.nextWeek',
              none: 'tickets.answer.due.none'
            },
            describe: 'a due-date window; a week is Sunday..Saturday; none = no due date'
          },
          drafts: {
            type: 'boolean',
            describe: 'true adds drafts (tasks outside every board column) to the open tasks'
          },
          search: { type: 'string', describe: 'words in the task title' },
          layout: {
            type: 'enum',
            values: ['list', 'board', 'canvas'],
            default: 'list',
            describe: 'board = status columns; canvas = where the /tasks board places each task'
          },
          limit: { type: 'number', min: 1, max: 50, default: 20 }
        },
        // Before the view resolves its names (the body's own `openRoute` takes
        // over then): the filters the board's URL carries as they are.
        link: { route: '/tasks', query: { type: 'type', q: 'search' } }
      },
      {
        id: 'tickets-roadmap',
        title: 'tickets.roadmapAnswer.title',
        describe:
          'The roadmap: cycles and epics on a timeline with their dates and progress; show = cycles, epics or both; search = words in a name.',
        component: 'TicketsRoadmapView',
        icon: 'target',
        hue: 'tickets',
        size: 'l',
        params: {
          show: {
            type: 'enum',
            values: ['all', 'cycles', 'epics'],
            labels: {
              all: 'tickets.roadmapAnswer.show.all',
              cycles: 'tickets.roadmapAnswer.show.cycles',
              epics: 'tickets.roadmapAnswer.show.epics'
            },
            default: 'all'
          },
          status: {
            type: 'enum',
            values: ['current', 'all'],
            default: 'current',
            describe:
              'current = active and planned cycles, epics not done (default); all = released, archived and done too'
          },
          search: { type: 'string', describe: 'words in the cycle or epic name' },
          limit: { type: 'number', min: 1, max: 30, default: 12 }
        },
        link: { route: '/tasks/roadmap', query: {} }
      }
    ]
  }
}

// The view's params → the board filter: an assignee NAME resolves through the
// roster to an email (a name nobody has matches nothing — never every task);
// a due window becomes days in the member's clock. The project, epic and
// cycle NAMES ride as `<name>Name` for ticketsAnswerResolve. The canvas layout
// reads only the tasks the board places on its canvas (`placed`).
export function ticketsAnswerFilters(root, params, now) {
  const el = this
  const r = root || {}
  const p = params && typeof params === 'object' ? params : {}
  const out = { status: typeof p.status === 'string' && p.status ? p.status : 'open' }
  const ws = r.workspace && (r.workspace.id || r.workspace._id)
  if (ws) out.workspaceId = String(ws)
  const who = typeof p.assignee === 'string' ? p.assignee.trim() : ''
  if (who) {
    const low = who.toLowerCase()
    if (low === 'me') out.assignee = String(r.currentUserEmail || '') || '\u0000'
    else if (low === 'team' || low === 'agents') {
      // The board's own categories ('team' leaves the member out).
      out.assignee = low
      if (r.currentUserEmail) out.currentUserEmail = String(r.currentUserEmail)
    } else if (low.includes('@')) out.assignee = low
    else {
      const people = Array.isArray(r.people) ? r.people : []
      const hit = people.find((x) => {
        const name = String(
          (x && (x.name || [x.firstName, x.lastName].filter(Boolean).join(' '))) || ''
        ).toLowerCase()
        return name && (name === low || name.split(' ').includes(low))
      })
      out.assignee = hit && hit.email ? String(hit.email) : '\u0000'
    }
  }
  if (typeof p.priority === 'string' && p.priority) out.priority = p.priority
  if (typeof p.type === 'string' && p.type) out.issueType = p.type
  if (typeof p.search === 'string' && p.search.trim()) out.search = p.search.trim()
  for (const name of ['project', 'epic', 'cycle']) {
    const v = typeof p[name] === 'string' ? p[name].trim() : ''
    if (v) out[name + 'Name'] = v
  }
  if (p.drafts === true) out.drafts = true
  if (typeof p.due === 'string' && p.due) {
    const window =
      el && typeof el.call === 'function' ? el.call('ticketsAnswerDueWindow', p.due, now) : null
    if (window) out.due = window
  }
  if (p.layout === 'canvas') out.placed = true
  return out
}

// A due window in the member's clock: `{ from, to, none, open }`, days as
// YYYY-MM-DD — the day a task's due date names (the app shows and edits that
// day). A WEEK is the workspace's own week, Sunday..Saturday — the calendar's
// week grid and its agenda presets (calendar functions/calendarAgenda.js), so
// a Fly page's tasks and meetings "this week" are the same days. Overdue is
// any day before today and never a done task. null for a window nobody
// declared.
export function ticketsAnswerDueWindow(due, now) {
  const base = now instanceof Date ? now : new Date(Number(now) || Date.now())
  const pad = (n) => String(n).padStart(2, '0')
  const day = (shift) => {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + shift)
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
  }
  const sinceStart = base.getDay()
  if (due === 'overdue') return { from: null, to: day(-1), none: false, open: true }
  if (due === 'today') return { from: day(0), to: day(0), none: false, open: false }
  if (due === 'thisWeek') {
    return { from: day(-sinceStart), to: day(6 - sinceStart), none: false, open: false }
  }
  if (due === 'nextWeek') {
    return { from: day(7 - sinceStart), to: day(13 - sinceStart), none: false, open: false }
  }
  if (due === 'none') return { from: null, to: null, none: true, open: false }
  return null
}

// The NAMES an ask gave → what the reads filter on: a project → its id (the
// task↔project link, Ticket.project), an epic → the exact name the tasks
// carry, a cycle → its id ('current' = the active cycle). Matching: the same
// name or key in any case, else the ONE name that starts with it, else the ONE
// that holds it. `{ filters }`; `unknown: true` when nothing here answers to a
// name (the view then says no task matches — never every task); `failed:
// true` when a lookup could not be read.
export async function ticketsAnswerResolve(el, filters) {
  const host = el || this
  const f = { ...(filters || {}) }
  const wantProject = f.projectName || ''
  const wantEpic = f.epicName || ''
  const wantCycle = f.cycleName || ''
  delete f.projectName
  delete f.epicName
  delete f.cycleName
  if (!wantProject && !wantEpic && !wantCycle) return { filters: f }
  const norm = (v) =>
    String(v || '')
      .trim()
      .toLowerCase()
  const pick = (items, wanted, namesOf) => {
    const w = norm(wanted)
    const all = (Array.isArray(items) ? items : []).filter(Boolean)
    const by = (test) => all.filter((it) => namesOf(it).some((n) => n && test(norm(n))))
    const same = by((n) => n === w)
    if (same.length) return same[0]
    const starts = by((n) => n.startsWith(w))
    if (starts.length === 1) return starts[0]
    const holds = by((n) => n.includes(w))
    return holds.length === 1 ? holds[0] : null
  }
  const fns = (host && host.context && host.context.functions) || {}
  const service = async () => {
    const sdk = host.sdk || host.call('getSDK')
    return sdk && typeof sdk.getService === 'function' ? sdk.getService('tickets') : null
  }
  let failed = false
  let unknown = false
  const jobs = []
  if (wantProject) {
    jobs.push(
      (async () => {
        if (typeof fns.canvasAnswerRead !== 'function') {
          failed = true
          return
        }
        const res = await host.call('canvasAnswerRead', host)
        if (!res || res.failed || !Array.isArray(res.projects)) {
          failed = true
          return
        }
        const hit = pick(res.projects, wantProject, (x) => [x.key, x.name])
        const id = hit && (hit._id || hit.id)
        if (!id) unknown = true
        else f.project = String(id)
      })()
    )
  }
  if (wantEpic) {
    jobs.push(
      (async () => {
        const tSvc = await service()
        if (!tSvc || typeof tSvc.epicCounts !== 'function') {
          failed = true
          return
        }
        // `{ [epic name]: { total, done, … } }` — the names the tasks carry.
        const res = await tSvc.epicCounts()
        const bag = res && typeof res === 'object' && !Array.isArray(res) ? res : {}
        const names = Object.keys(bag).filter((n) => n && n !== '__none__')
        const hit = pick(names, wantEpic, (n) => [n])
        if (!hit) unknown = true
        else f.epic = hit
      })()
    )
  }
  if (wantCycle) {
    jobs.push(
      (async () => {
        const tSvc = await service()
        if (!tSvc || !tSvc.cycle || typeof tSvc.cycle.list !== 'function') {
          failed = true
          return
        }
        const res = await tSvc.cycle.list()
        const rows = Array.isArray(res) ? res : (res && res.data) || []
        const hit =
          norm(wantCycle) === 'current'
            ? rows.find((c) => c && c.status === 'active') || null
            : pick(rows, wantCycle, (c) => [c.name])
        const id = hit && (hit._id || hit.id)
        if (!id) unknown = true
        else f.cycleId = String(id)
      })()
    )
  }
  try {
    await Promise.all(jobs)
  } catch (err) {
    console.warn('[ticketsAnswer] name lookup failed:', err && err.message)
    failed = true
  }
  return failed
    ? { filters: f, failed: true }
    : unknown
      ? { filters: f, unknown: true }
      : { filters: f }
}

// ↗ Open: the /tasks board's own URL for what the view shows — the filters
// the board reads from its address (pages/board.js _filtersFromUrl): type,
// assignee (its me / team / agents, or the email the name resolved to), epic,
// q, cycle, project. The board has no status, priority or due-date filter;
// those stay with the view. A project or cycle nothing answered to is left
// out (the board reads an id there).
export function ticketsAnswerRoute(filters, params) {
  const f = filters && typeof filters === 'object' ? filters : {}
  const p = params && typeof params === 'object' ? params : {}
  const parts = []
  const add = (key, value) => {
    if (value == null || value === '') return
    parts.push(key + '=' + encodeURIComponent(String(value)))
  }
  add('type', f.issueType)
  const said = typeof p.assignee === 'string' ? p.assignee.trim() : ''
  const low = said.toLowerCase()
  if (low === 'me' || low === 'team' || low === 'agents') add('assignee', low)
  else if (said) add('assignee', f.assignee && f.assignee !== '\u0000' ? f.assignee : said)
  add('epic', f.epic || (typeof p.epic === 'string' ? p.epic.trim() : ''))
  add('q', f.search)
  add('cycle', f.cycleId)
  add('project', f.project)
  return '/tasks' + (parts.length ? '?' + parts.join('&') : '')
}

// The board layout's columns: every status column the status covers, in the
// board's own order (its columns, else the server's, else the standard
// workflow) — never only the columns a sample happened to hold (QA on dev:
// the empty ones were skipped). The Drafts lane joins for 'all', 'draft' and
// open + drafts. `[{ key, label }]`.
export async function ticketsAnswerBoardColumns(el, filters) {
  const host = el || this
  const root = (host && host.state && host.state.root) || {}
  const f = filters && typeof filters === 'object' ? filters : {}
  const usable = (c) => c && c.key && c.key !== 'unsynced' && c.key !== 'draft'
  let cols = (Array.isArray(root.columns) ? root.columns : []).filter(usable)
  if (!cols.length) {
    try {
      const sdk = host.sdk || host.call('getSDK')
      const tSvc =
        sdk && typeof sdk.getService === 'function' ? await sdk.getService('tickets') : null
      const res = tSvc && tSvc.columns ? await tSvc.columns.list() : null
      const rows = Array.isArray(res) ? res : (res && res.data) || []
      cols = rows.filter(usable)
    } catch (err) {
      console.warn('[ticketsAnswer] columns read failed:', err && err.message)
      cols = []
    }
  }
  if (!cols.length) {
    cols = ['backlog', 'todo', 'in_progress', 'blocked', 'ready_to_test', 'review', 'done'].map(
      (key, position) => ({ key, position })
    )
  }
  const list = cols
    .slice()
    .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
    .map((c) => ({
      key: String(c.key),
      // A seeded default column reads in the active language.
      label:
        host.call('columnLabelText', c) ||
        c.label ||
        c.name ||
        host.call('stateLabelText', host, c.key)
    }))
  const draft = {
    key: 'draft',
    label: String(host.call('polyglot', 'tickets.kanban.draftsColumn'))
  }
  const status = f.status || 'open'
  if (status === 'draft') return [draft]
  if (status === 'all') return list.concat(draft)
  if (status === 'open') {
    const open = list.filter((c) => c.key !== 'done')
    return f.drafts === true ? open.concat(draft) : open
  }
  const one = list.find((c) => c.key === status)
  return [one || { key: status, label: host.call('stateLabelText', host, status) }]
}

// Load the view's tasks into ITS OWN state (never the board's root lists) —
// once per params; a new params set reloads. `{ tickets, total, columns,
// failed, route }`: `total` is how many match when it is known; `columns` the
// board layout's columns with their counts.
export async function ticketsAnswerLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const root = host.state.root
  const params = (host.scope && host.scope.params) || {}
  const layout = host.call('ticketsAnswerLayout', host)
  const limit = Math.max(1, Math.min(50, Math.floor(Number(params.limit)) || 20))
  const asked = host.call('ticketsAnswerFilters', root, params)
  const sig = JSON.stringify([asked, limit, layout])
  if (host.__ticketsAnswerSig === sig) return null
  host.__ticketsAnswerSig = sig
  const current = () => host.__ticketsAnswerSig === sig
  const resolved = (await host.call('ticketsAnswerResolve', host, asked)) || { filters: asked }
  if (!current()) return null
  const filters = resolved.filters || asked
  const route = host.call('ticketsAnswerRoute', filters, params)
  if (resolved.failed || resolved.unknown) {
    host.state.update({
      tickets: [],
      total: 0,
      columns: null,
      layout,
      limit,
      failed: !!resolved.failed,
      route
    })
    return null
  }
  if (layout === 'board') {
    const cols = await host.call('ticketsAnswerBoardColumns', host, filters)
    if (!current()) return null
    if (!filters.due) {
      // Each column its own first cards (the board's column read, in the
      // board's order) and its full count under the same filters.
      const per = Math.max(2, Math.min(10, Math.ceil(limit / Math.max(1, cols.length))))
      const [pages, counts] = await Promise.all([
        Promise.all(
          cols.map((c) => host.call('fetchColumnPage', host, c.key, 0, per, filters, 'answer'))
        ),
        host.call(
          'fetchColumnCounts',
          host,
          filters,
          cols.map((c) => c.key),
          { strict: true }
        )
      ])
      if (!current()) return null
      const tickets = pages.reduce((all, page) => all.concat(Array.isArray(page) ? page : []), [])
      const columns = cols.map((c) => ({
        ...c,
        count: counts && typeof counts[c.key] === 'number' ? counts[c.key] : null
      }))
      const known = columns.every((c) => typeof c.count === 'number')
      host.state.update({
        tickets,
        total: known ? columns.reduce((n, c) => n + c.count, 0) : null,
        columns,
        layout,
        limit,
        // A column read answers [] on a failure; with no count either, the
        // view cannot tell "nothing matches" — it says the read failed.
        failed: !counts && !tickets.length,
        route
      })
      return tickets
    }
    // A due window is read here, not by the server: its matches, grouped —
    // the counts are the full set when the read reached the end.
    const list = await host.call('fetchTicketsForAnswer', host, filters, limit)
    if (!current()) return null
    const byColumn = list && list.byColumn
    const columns = cols.map((c) => ({
      ...c,
      count: byColumn && typeof byColumn === 'object' ? byColumn[c.key] || 0 : null
    }))
    host.state.update({
      tickets: Array.isArray(list) ? list : [],
      total: list && typeof list.total === 'number' ? list.total : null,
      columns,
      layout,
      limit,
      failed: !!(list && list.failed),
      route
    })
    return list
  }
  // The canvas: the tasks the board places on its canvas — drafts by the
  // board's own rule (ticketUtils ticketPlacement: outside a column ⟺ draft).
  const read =
    layout === 'canvas' && (filters.status === 'open' || filters.status === 'all')
      ? { ...filters, status: 'draft' }
      : filters
  const list = await host.call('fetchTicketsForAnswer', host, read, limit)
  if (!current()) return null
  host.state.update({
    tickets: Array.isArray(list) ? list : [],
    total: list && typeof list.total === 'number' ? list.total : null,
    columns: null,
    layout,
    limit,
    failed: !!(list && list.failed),
    route
  })
  return list
}

// LIST rows (HomeWidgetRow): title, "status · priority", the assignee's
// initials; the row opens the ticket's own sheet.
export function ticketsAnswerRows(tickets) {
  const el = this
  const list = Array.isArray(tickets) ? tickets : []
  const root = el && el.state && el.state.root
  const columns = root && Array.isArray(root.columns) ? root.columns : []
  const statusOf = (t) => {
    if (t.columnKey === 'draft') return el.call('polyglot', 'tickets.kanban.draftsColumn')
    const col = columns.find((c) => c && c.key === t.columnKey)
    return (
      (col && (el.call('columnLabelText', col) || col.label)) ||
      el.call('stateLabelText', el, t.state || t.columnKey)
    )
  }
  const initials = (email) => {
    const name = String(email || '')
      .split('@')[0]
      .split(/[._-]+/)
      .filter(Boolean)
    return name
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('')
  }
  return list.map((t) => ({
    key: String(t.id),
    title: t.title || String(el.call('polyglot', 'tickets.answer.untitled')),
    meta: [statusOf(t), el.call('priorityLabel', t.priority)].filter(Boolean).join(' · '),
    ...(t.assigneeEmail ? { initials: initials(String(t.assigneeEmail).split(',')[0]) } : {}),
    open: { call: 'openTicketDetailModal', arg: t }
  }))
}

// BOARD columns. With the view's `columns` (the board read): every one of
// them, in that order, with its count — `sample: false` — and how many more
// it holds than it shows. Without (an older caller): the columns the tasks
// hold, in the board's order, counting what is shown (`sample: true`).
export function ticketsAnswerColumns(tickets, columns) {
  const el = this
  const list = Array.isArray(tickets) ? tickets : []
  const root = el && el.state && el.state.root
  const known = root && Array.isArray(root.columns) ? root.columns : []
  const labelOf = (key) => {
    if (key === 'draft') return el.call('polyglot', 'tickets.kanban.draftsColumn')
    const col = known.find((c) => c && c.key === key)
    return (
      (col && (el.call('columnLabelText', col) || col.label)) || el.call('stateLabelText', el, key)
    )
  }
  let cols = Array.isArray(columns) && columns.length ? columns : null
  if (!cols) {
    const ORDER = [
      'backlog',
      'todo',
      'in_progress',
      'blocked',
      'ready_to_test',
      'review',
      'done',
      'draft'
    ]
    cols = [...new Set(list.map((t) => t.columnKey || 'backlog'))]
      .sort((a, b) => {
        const ia = ORDER.indexOf(a)
        const ib = ORDER.indexOf(b)
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
      })
      .map((key) => ({ key, label: labelOf(key), count: null }))
  }
  return cols.map((c) => {
    const items = list.filter((t) => (t.columnKey || 'backlog') === c.key)
    const full = typeof c.count === 'number'
    return {
      key: c.key,
      label: c.label || labelOf(c.key),
      count: full ? c.count : items.length,
      sample: !full,
      more: full ? Math.max(0, c.count - items.length) : 0,
      tickets: items
    }
  })
}

// The layout the view draws: its `layout` param, list by default.
export function ticketsAnswerLayout(el) {
  const host = el || this
  const p = (host && host.scope && host.scope.params) || {}
  return p.layout === 'board' || p.layout === 'canvas' ? p.layout : 'list'
}

// THE CANVAS — the tasks where the /tasks board places them (ticketCanvasPos,
// the board's ONE placement read), in ems of the board's own type (`base`,
// the px one em is where the board renders — measured by
// ticketsAnswerCanvasFit). The board is endless: live on dev (system, 10
// drafts) its spots spanned 272 × 99 ems, and that whole extent scaled into a
// Fly card drew each card 51 px wide. So the EMPTY board folds: down each
// axis the cards' spans merge into bands — spans that overlap, or stand
// closer than the scene's gap, are one band and keep their spacing — and the
// board between two bands becomes the scene's one gap (its grid's, a spacing
// token). A card is its band's column and row (1-based) and its spot inside
// them; every card keeps its order both ways. A span is the card's measured
// size, else its board width or the default card, 24 × 9 ems, until measured
// (`canvas.sizes`). In reading order — row by row, left to right. Every key a
// card reads is set (a childrenAs-'state' card falls back to ROOT for a
// missing one).
export function ticketsAnswerCanvasCards(tickets, canvas) {
  const el = this
  const root = (el && el.state && el.state.root) || null
  const c = canvas && typeof canvas === 'object' ? canvas : {}
  const unit = Number(c.base) > 0 ? Number(c.base) : 16
  const gap = Number(c.gap) > 0 ? Number(c.gap) : 0
  const sizes = c.sizes && typeof c.sizes === 'object' ? c.sizes : {}
  const placed = []
  for (const t of Array.isArray(tickets) ? tickets : []) {
    const p = el.call('ticketCanvasPos', root, t)
    const x = p ? Number(p.x) : NaN
    const y = p ? Number(p.y) : NaN
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue
    const w = Number(p.width)
    placed.push({ t, x, y, w: Number.isFinite(w) && w > 0 ? w : 0 })
  }
  if (!placed.length) return []
  const minX = Math.min(...placed.map((p) => p.x))
  const minY = Math.min(...placed.map((p) => p.y))
  const round = (v) => Math.round(v * 100) / 100
  const items = placed.map(({ t, x, y, w }) => {
    const key = String(t.id)
    const size = sizes[key] && typeof sizes[key] === 'object' ? sizes[key] : {}
    const width = w ? round(w / unit) : null
    return {
      t,
      key,
      left: round((x - minX) / unit),
      top: round((y - minY) / unit),
      width,
      spanW: Number(size.w) > 0 ? Number(size.w) : width || 24,
      spanH: Number(size.h) > 0 ? Number(size.h) : 9
    }
  })
  // One axis: the band each card falls in and its offset inside it.
  const fold = (start, span) => {
    const band = []
    const offset = []
    let index = -1
    let from = 0
    let to = 0
    const order = items.map((it, i) => i).sort((a, b) => start(items[a]) - start(items[b]))
    for (const i of order) {
      const at = start(items[i])
      if (index < 0 || at > to + gap) {
        index++
        from = at
        to = at + span(items[i])
      } else {
        to = Math.max(to, at + span(items[i]))
      }
      band[i] = index
      offset[i] = round(at - from)
    }
    return { band, offset }
  }
  const across = fold(
    (it) => it.left,
    (it) => it.spanW
  )
  const down = fold(
    (it) => it.top,
    (it) => it.spanH
  )
  return items
    .map((it, i) => ({
      ...it.t,
      key: it.key,
      canvasCol: across.band[i] + 1,
      canvasRow: down.band[i] + 1,
      canvasLeft: across.offset[i],
      canvasTop: down.offset[i],
      canvasWidth: it.width,
      canvasSpanW: it.spanW,
      canvasSpanH: it.spanH
    }))
    .sort(
      (a, b) =>
        a.canvasRow - b.canvasRow ||
        a.canvasCol - b.canvasCol ||
        a.canvasTop - b.canvasTop ||
        a.canvasLeft - b.canvasLeft
    )
}

// The canvas scene's box in ems: its measured extent (`canvas`, written by
// ticketsAnswerCanvasFit), else the bands it holds — each column as wide as
// its widest card reaches, each row as tall as its tallest, the scene's gap
// between them (none until measured). `{ w, h }`, never 0.
export function ticketsAnswerCanvasBox(cards, canvas) {
  const c = canvas && typeof canvas === 'object' ? canvas : {}
  if (c.w > 0 && c.h > 0) return { w: c.w, h: c.h }
  const gap = Number(c.gap) > 0 ? Number(c.gap) : 0
  const cols = {}
  const rows = {}
  for (const card of Array.isArray(cards) ? cards : []) {
    const col = Number(card.canvasCol) || 1
    const row = Number(card.canvasRow) || 1
    const right = (Number(card.canvasLeft) || 0) + (Number(card.canvasSpanW) || 24)
    const bottom = (Number(card.canvasTop) || 0) + (Number(card.canvasSpanH) || 9)
    cols[col] = Math.max(cols[col] || 0, right)
    rows[row] = Math.max(rows[row] || 0, bottom)
  }
  const sum = (bands) => {
    const sizes = Object.values(bands)
    return sizes.reduce((a, b) => a + b, 0) + Math.max(0, sizes.length - 1) * gap
  }
  const round = (v) => Math.round(v * 100) / 100
  return { w: round(sum(cols)) || 24, h: round(sum(rows)) || 9 }
}

// Fit the canvas scene to what it holds: the board's em in px (`base`, the
// view's own type — the scale the board renders at), the scene's gap, its
// extent and each card's size, in ems — measured, since a card is as tall as
// its words and the gap is a token. Written to the view's state only when it
// moved, so the fit settles: a card's size in ems does not depend on where it
// sits or on the scale. The scene's font size scales it to the card
// (components/TicketsAnswerView.js). `scene` is the scene element; its state
// is the view's. Reads geometry only, writes none.
export function ticketsAnswerCanvasFit(scene) {
  const el = scene || this
  const node = el && el.node
  const s = el && el.state
  if (!node || !s || typeof s.update !== 'function' || !node.isConnected) return false
  const view = node.ownerDocument && node.ownerDocument.defaultView
  const host = el.parent && el.parent.node
  if (!view || !host || typeof view.getComputedStyle !== 'function') return false
  const style = view.getComputedStyle(node)
  const base = parseFloat(view.getComputedStyle(host).fontSize) || 0
  const em = parseFloat(style.fontSize) || 0
  if (!(base > 0) || !(em > 0) || !node.offsetWidth) return false
  const round = (v) => Math.ceil(v * 10) / 10
  const sizes = {}
  for (const child of typeof el.getChildren === 'function' ? el.getChildren() : []) {
    const n = child && child.node
    const id = child && child.state ? child.state.id : null
    if (!n || !n.offsetWidth || id === null || id === undefined) continue
    sizes[String(id)] = { w: round(n.offsetWidth / em), h: round(n.offsetHeight / em) }
  }
  const next = {
    base,
    gap: Math.round(((parseFloat(style.columnGap) || 0) / em) * 100) / 100,
    w: round(node.offsetWidth / em),
    h: round(node.offsetHeight / em),
    sizes
  }
  const cur = s.canvas || {}
  const near = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.2
  const known = cur.sizes || {}
  const same =
    cur.base === next.base &&
    near(cur.gap, next.gap) &&
    near(cur.w, next.w) &&
    near(cur.h, next.h) &&
    Object.keys(sizes).every(
      (id) => known[id] && near(known[id].w, sizes[id].w) && near(known[id].h, sizes[id].h)
    )
  if (same) return false
  s.update({ canvas: next })
  return true
}

// The MORE line's words — under a list or a canvas that shows PART of what
// matches: "20 of 216 shown — the rest are in Tasks" when the read knows how
// many match (`total`); without a total, the one sentence under a FULL page
// of rows ("The 20 most recently updated — the rest are in Tasks"), a page as
// long as its limit being the newest N of possibly more. '' for anything else
// — reading, failed, empty, everything shown, fewer rows than the limit, the
// board (it says it per column) — and the line hides on ''. Before, only the
// line's `hide` knew the rule: an empty card still carried "The 0 most
// recently updated…" as its (hidden) text, and QA read it in the card (Fly QA
// 2026-10-18, dev 15f497576). `el` is the line; the limit is the view's.
export function ticketsAnswerMoreText(el, s) {
  const host = el || this
  const list = s && Array.isArray(s.tickets) ? s.tickets : null
  if (!list || s.failed || !list.length || s.layout === 'board') return ''
  if (typeof s.total === 'number') {
    if (s.total <= list.length) return ''
    return String(host.call('polyglot', 'tickets.answer.moreOf'))
      .replace('{n}', String(list.length))
      .replace('{total}', String(s.total))
  }
  const view = host && host.parent
  const p = (view && view.scope && view.scope.params) || {}
  const limit = Number(s.limit) || Number(p.limit) || 20
  if (list.length < limit) return ''
  return String(host.call('polyglot', 'tickets.answer.more')).replace('{n}', String(list.length))
}
